import { createDefaultWidgetData } from '../utils/widgetDefaults';
import type { BasicBlogWidgetData } from '../types/blog';
import type { EventWidgetData } from '../types/event';
import type { BasicComboData, ClassicComboData, PrimeComboData } from '../types/combo';
import type { BasicPopularItemsData, ClassicPopularItemsData, PrimePopularItemsData } from '../types/popular';
import type { BannerSlideData } from '../components/banner/types';

// Default Classic Banner data with rich HTML content (single slide with fade animations)
export const DEFAULT_CLASSIC_BANNER_DATA: BannerSlideData[] = [
  {
    id: 'banner-classic-default',
    title: '<span class="text-white">Welcome to</span> <span class="text-red-500 font-bold">Our Restaurant</span>',
    subtitle: '<em class="text-yellow-300">Authentic Flavors</em>, <strong class="text-white">Memorable Experiences</strong>',
    description: 'Discover the <strong class="text-yellow-200">finest cuisine</strong> crafted with <em class="text-blue-200">passion</em> and served with <span class="text-green-200">excellence</span>. Every dish tells a story of <strong>tradition</strong>, <em>quality</em>, and culinary <span class="text-orange-200">artistry</span>.',
    backgroundImage: '',
    backgroundColor: '#1a1a1a',
    textAlignment: 'center',
    textColor: '#ffffff',
    overlayOpacity: 0.3,
    overlayColor: 'rgba(0, 0, 0, 0.3)',
    ctaButtons: [
      {
        id: 'cta-primary',
        text: '<strong>Order Online</strong>',
        url: '#order',
        variant: 'primary',
        backgroundColor: '#dc2626',
        color: '#ffffff',
        outlineColor: '#dc2626',
        visible: true
      },
      {
        id: 'cta-secondary',
        text: '<em>View Menu</em>',
        url: '#menu',
        variant: 'outline',
        color: '#ffffff',
        outlineColor: '#ffffff',
        visible: true
      }
    ]
  }
];

// Default Essential Banner data with rich HTML content
export const DEFAULT_ESSENTIAL_BANNER_DATA: BannerSlideData[] = [
  {
    id: 'banner-essential-default',
    title: '<span class="text-white">Welcome to</span> <span class="text-red-500 font-bold">Our Restaurant</span>',
    subtitle: '<em class="text-yellow-300">Authentic Flavors</em>, <strong class="text-white">Memorable Experiences</strong>',
    description: 'Discover the <strong class="text-yellow-200">finest cuisine</strong> crafted with <em class="text-blue-200">passion</em> and served with <span class="text-green-200">excellence</span>. Every dish tells a story of <strong>tradition</strong>, <em>quality</em>, and culinary <span class="text-orange-200">artistry</span>.',
    backgroundImage: '',
    backgroundColor: '#1a1a1a',
    textAlignment: 'center',
    textColor: '#ffffff',
    overlayOpacity: 0.3,
    overlayColor: 'rgba(0, 0, 0, 0.3)',
    ctaButtons: [
      {
        id: 'cta-primary',
        text: '<strong>Order Online</strong>',
        url: '#order',
        variant: 'outline',
        backgroundColor: '#dc2626',
        color: '#ffffff',
        outlineColor: '#dc2626',
        visible: true
      },
      {
        id: 'cta-secondary',
        text: '<em>View Menu</em>',
        url: '#menu',
        variant: 'outline',
        color: '#ffffff',
        outlineColor: '#ffffff',
        visible: true
      }
    ]
  }
];

// Empty default combo data structures with proper types
export const DEFAULT_EMPTY_COMBO_DATA: BasicComboData = {
  content: '<h2 class="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Our <span class="text-red-600">Special</span> Combos</h2><p class="text-gray-600 text-lg max-w-2xl mx-auto">Discover our amazing combo deals, perfect for sharing</p>',
  backgroundColor: "#ffffff",
  combos: [],
  emptyStateContent: '<h3 class="text-lg sm:text-xl font-semibold mb-2">No Combos Available</h3><p class="text-sm sm:text-base max-w-md">Our combo deals will be displayed here soon.</p>'
};

export const DEFAULT_EMPTY_CLASSIC_COMBO_DATA: ClassicComboData = {
  content: '<h2 class="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Our <span class="text-red-600">Special</span> Combos</h2><p class="text-gray-600 text-lg max-w-2xl mx-auto">Discover our amazing combo deals, perfect for sharing</p>',
  backgroundColor: "#ffffff",
  combos: [],
  emptyStateContent: '<h3 class="text-lg sm:text-xl font-semibold mb-2">No Combos Available</h3><p class="text-sm sm:text-base max-w-md">Our combo deals will be displayed here soon.</p>'
};

export const DEFAULT_EMPTY_PRIME_COMBO_DATA: PrimeComboData = {
  content: '<h2 class="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Our <span class="text-red-600">Premium</span> Combos</h2><p class="text-gray-600 text-lg max-w-2xl mx-auto">Discover our premium combo deals with exclusive offerings</p>',
  backgroundColor: "#ffffff",
  combos: [],
  emptyStateContent: '<h3 class="text-lg sm:text-xl font-semibold mb-2">No Premium Combos Available</h3><p class="text-sm sm:text-base max-w-md">Our premium combo deals will be displayed here soon.</p>'
};

// Sample combo data with realistic content
export const DEFAULT_BASIC_COMBO_DATA: BasicComboData = {
  content: '<h2 class="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Our <span class="text-red-600">Special</span> Combos</h2><p class="text-gray-600 text-lg max-w-2xl mx-auto">Discover our amazing combo deals, perfect for sharing</p>',
  backgroundColor: "#ffffff",
  combos: [],
  emptyStateContent: '<h3 class="text-lg sm:text-xl font-semibold mb-2">No Combos Available</h3><p class="text-sm sm:text-base max-w-md">Our combo deals will be displayed here soon.</p>'
};

export const DEFAULT_CLASSIC_COMBO_DATA: ClassicComboData = {
  content: '<h2 class="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Our <span class="text-red-600">Special</span> Combos</h2><p class="text-gray-600 text-lg max-w-2xl mx-auto">Discover our amazing combo deals, perfect for sharing</p>',
  backgroundColor: "#ffffff",
  combos: [],
  emptyStateContent: '<h3 class="text-lg sm:text-xl font-semibold mb-2">No Combos Available</h3><p class="text-sm sm:text-base max-w-md">Our combo deals will be displayed here soon.</p>'
};

export const DEFAULT_PRIME_COMBO_DATA: PrimeComboData = {
  content: '<h2 class="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Our <span class="text-red-600">Premium</span> Combos</h2><p class="text-gray-600 text-lg max-w-2xl mx-auto">Discover our premium combo deals with exclusive offerings</p>',
  backgroundColor: "#ffffff",
  combos: [],
  emptyStateContent: '<h3 class="text-lg sm:text-xl font-semibold mb-2">No Premium Combos Available</h3><p class="text-sm sm:text-base max-w-md">Our premium combo deals will be displayed here soon.</p>'
};

// Empty default popular items data structures with proper types
export const DEFAULT_EMPTY_POPULAR_ITEMS_DATA: BasicPopularItemsData = {
  content: '<h2 class="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Our <span class="text-red-600">Popular</span> Items</h2><p class="text-gray-600 text-lg max-w-2xl mx-auto">Discover our most loved dishes, crafted with passion and served with pride</p>',
  backgroundColor: "#ffffff",
  selectedItemIds: [],
  emptyStateContent: '<h3 class="text-lg sm:text-xl font-semibold mb-2">No Popular Items Available</h3><p class="text-sm sm:text-base max-w-md">Our popular items will be displayed here soon.</p>',
  menuData: []
};

export const DEFAULT_EMPTY_CLASSIC_POPULAR_ITEMS_DATA: ClassicPopularItemsData = {
  content: '<h2 class="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Our <span class="text-red-600">Popular</span> Items</h2><p class="text-gray-600 text-lg max-w-2xl mx-auto">Discover our most loved dishes, crafted with passion and served with pride</p>',
  backgroundColor: "#ffffff",
  selectedItemIds: [],
  emptyStateContent: '<h3 class="text-lg sm:text-xl font-semibold mb-2">No Popular Items Available</h3><p class="text-sm sm:text-base max-w-md">Our popular items will be displayed here soon.</p>',
  menuData: [],
  showRating: false
};

export const DEFAULT_EMPTY_PRIME_POPULAR_ITEMS_DATA: PrimePopularItemsData = {
  content: '<h2 class="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Our <span class="text-red-600">Premium</span> Popular Items</h2><p class="text-gray-600 text-lg max-w-2xl mx-auto">Discover our premium popular dishes with exclusive features</p>',
  backgroundColor: "#ffffff",
  selectedItemIds: [],
  emptyStateContent: '<h3 class="text-lg sm:text-xl font-semibold mb-2">No Premium Popular Items Available</h3><p class="text-sm sm:text-base max-w-md">Our premium popular items will be displayed here soon.</p>',
  menuData: [],
  showRating: true,
  premiumFeatures: true
};

// Enhanced default popular items data with empty arrays as default
export const DEFAULT_POPULAR_ITEMS_DATA = {
  type: "ClassicPopularItems",
  data: {
    content: '<h2 class="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Our <span class="text-red-600">Popular</span> Items</h2><p class="text-gray-600 text-lg max-w-2xl mx-auto">Discover our most loved dishes, crafted with passion and served with pride</p>',
    backgroundColor: "#ffffff",
    selectedItemIds: [],
    emptyStateContent: '<h3 class="text-lg sm:text-xl font-semibold mb-2">No Popular Items Available</h3><p class="text-sm sm:text-base max-w-md">Our popular items will be displayed here soon.</p>',
    showRating: false,
    menuData: []
  }
};

// Sample menu data for reference (not used in defaults but available for testing)

// Default navigation data for Paradise Biryani Pointe
export const DEFAULT_NAV_DATA = {
  type: "ClassicNav",
  data: {
    logoUrl: "https://snapmenu-restaurant-workers.raghavan-7fc.workers.dev/files/1756666894317-logo.png",
    logoText: "Paradise Biryani Pointe",
    items: [
      { id: "1", label: "Menu", url: "/menu", isEnabled: true },
      { id: "2", label: "Our Story", url: "/our-story", isEnabled: true },
      { id: "3", label: "Blog", url: "/blog", isEnabled: true },
      { id: "4", label: "Gallery", url: "/gallery", isEnabled: true },
      { id: "5", label: "Events", url: "/events", isEnabled: true },
      { id: "6", label: "Contact", url: "/contact", isEnabled: true },
    ],
    backgroundColor: "#050404ff",
    textColor: "#f8fafbff",
    contactInfo: "Call & Order:203-956-7133",
    showSearchIcon: false,
    showCartIcon: true,
    showOrderButton: false,
    orderButtonText: "Order Online",
    orderButtonColor: "#ef4444"
  }
};

// Default static data for different page types
export const DEFAULT_PAGE_WIDGETS = {
  contact: [
    {
      type: 'BasicContactForm',
      data: createDefaultWidgetData('BasicContactForm')
    }
  ],

  home: [
    {
      type: 'EssentialBanner',
      data: createDefaultWidgetData('EssentialBanner')
    },
    {
      type: 'ClassicPopularItems',
      data: DEFAULT_POPULAR_ITEMS_DATA.data
    },
    {
      type: 'EssentialTestimonials',
      data: createDefaultWidgetData('EssentialTestimonials')
    }
  ],

  story: [
    {
      type: 'BasicStory',
      data: createDefaultWidgetData('BasicStory')
    }
  ],

  blog: [
    {
      type: 'BasicBlog',
      data: createDefaultWidgetData('BasicBlog')
    }
  ],

  events: [
    {
      type: 'BasicEvents',
      data: createDefaultWidgetData('BasicEvents')
    }
  ],

  gallery: [
    {
      type: 'BasicGallery',
      data: createDefaultWidgetData('BasicGallery')
    }
  ]
};

// Enhanced gallery data with restaurant-specific content (simplified structure)
export const RESTAURANT_GALLERY_DATA = {
  type: 'BasicGallery',
  data: {
    section: {
      title: "Pizza Moments",
      subtitle: "Capturing the magic behind every bite",
      description: "Explore our culinary journey through these carefully curated moments that showcase the passion and artistry behind every dish we create."
    },
    images: [
      {
        id: '1',
        url: "https://st.depositphotos.com/1003814/5052/i/450/depositphotos_50523105-stock-photo-pizza-with-tomatoes.jpg",
        title: "Delicious Pizza",
        description: "Fresh ingredients and authentic flavors"
      },
      {
        id: '2',
        url: "https://images.unsplash.com/photo-1571997478779-2adcbbe9ab2f?w=500&h=500&fit=crop",
        title: "Cozy Atmosphere",
        description: "Perfect dining experience"
      },
      {
        id: '3',
        url: "https://images.unsplash.com/photo-1574071318508-1cdbab80d002?w=500&h=500&fit=crop",
        title: "Fresh Ingredients",
        description: "Quality you can taste"
      },
      {
        id: '4',
        url: "https://images.unsplash.com/photo-1590947132387-155cc02f3212?w=500&h=500&fit=crop",
        title: "Artisan Crafted",
        description: "Made with passion"
      },
      {
        id: '5',
        url: "https://images.unsplash.com/photo-1513104890138-7c749659a591?w=500&h=500&fit=crop",
        title: "Wood Fired",
        description: "Traditional cooking methods"
      },
      {
        id: '6',
        url: "https://images.unsplash.com/photo-1571407970349-bc81e7e96d47?w=500&h=500&fit=crop",
        title: "Happy Customers",
        description: "Creating memorable moments"
      },
    ],
    cta: {
      text: "Follow on Instagram",
      link: "https://instagram.com/restaurant"
    }
  }
};

// Enhanced contact form data with restaurant-specific content
export const RESTAURANT_CONTACT_DATA = {
  type: 'BasicContactForm',
  data: {
    title: '<h1 class="text-4xl md:text-5xl font-bold mb-4">Get In <span class="text-red-600">Touch</span></h1>',
    subtitle: '<p class="text-gray-600 text-lg leading-relaxed">We\'d love to hear from you! Whether you have questions about our menu, want to make a reservation, or need catering services, don\'t hesitate to reach out.</p>',
    backgroundColor: "#f9fafb",
    formBackgroundColor: "#ffffff",
    cardBackgroundColor: "#ffffff",
    textColor: "#1f2937",
    primaryColor: "#dc2626",
    submitButtonText: "<span><strong>Send Message</strong></span>",
    successMessage: "<p><strong>Thank you for your message!</strong> We'll get back to you within 24 hours.</p>",
    errorMessage: "<p><strong>Something went wrong.</strong> Please try again or call us directly.</p>",
    contactInfo: {
      email: {
        address: "<a href='mailto:info@restaurant.com' class='text-red-600 hover:text-red-700'>info@restaurant.com</a>",
        description: "<p>Send us an <em>email</em> anytime! We respond within 24 hours.</p>",
        title: "<h3 class='text-xl font-semibold text-gray-800'>Email Us</h3>",
      },
      phone: {
        number: "<a href='tel:+15551234567' class='text-red-600 hover:text-red-700'>(555) 123-4567</a>",
        description: "<p><strong>Call us:</strong> Mon-Thu 11am-10pm | Fri-Sat 11am-11pm | Sun 12pm-9pm</p>",
        title: "<h3 class='text-xl font-semibold text-gray-800'>Call Us</h3>",
      },
      address: {
        full: "<address class='not-italic'>123 Main Street<br>Downtown District<br>City, State 12345</address>",
        description: "<p>Come <strong>visit</strong> our restaurant! We're located in the heart of downtown.</p>",
        title: "<h3 class='text-xl font-semibold text-gray-800'>Visit Us</h3>",
      },
    },
    formFields: {
      name: {
        enabled: true,
        required: true,
        placeholder: "Your Full Name",
        label: "Name",
      },
      email: {
        enabled: true,
        required: true,
        placeholder: "your.email@example.com",
        label: "Email Address",
      },
      phone: {
        enabled: true,
        required: false,
        placeholder: "(555) 123-4567",
        label: "Phone Number (Optional)",
      },
      message: {
        enabled: true,
        required: true,
        placeholder: "Tell us about your inquiry, reservation request, or how we can help you...",
        label: "Message",
      },
    },
  }
};

// Enhanced event data with restaurant-specific content
export const RESTAURANT_EVENT_DATA: EventWidgetData = {
  section: {
    title: "The place to celebrate",
    subtitle: "Join us for special occasions and memorable moments"
  },
  items: [
    {
      id: '1',
      title: 'Wine Tasting Evening',
      description: 'Join us for an exclusive wine tasting featuring premium selections from local vineyards.',
      image: 'https://images.unsplash.com/photo-1510812431401-41d2bd2722f3?w=400&h=250&fit=crop',
      date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 7 days from now
      time: '18:00',
      location: 'Main Dining Room',
      isPublished: true,
    },
    {
      id: '2',
      title: "Chef's Table Experience",
      description: 'An intimate dining experience with our head chef showcasing seasonal specialties.',
      image: 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=400&h=250&fit=crop',
      date: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 14 days from now
      time: '19:30',
      location: 'Private Dining Area',
      isPublished: true,
    },
    {
      id: '3',
      title: 'Live Jazz Night',
      description: 'Enjoy smooth jazz melodies while savoring our signature dishes and cocktails.',
      image: 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=400&h=250&fit=crop',
      date: new Date(Date.now() + 21 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 21 days from now
      time: '20:00',
      location: 'Lounge Area',
      isPublished: true,
    },
  ],
  isEnabled: true,
};

// Enhanced story data with restaurant-specific content
export const RESTAURANT_STORY_DATA = {
  type: 'BasicStory',
  data: {
    hero: {
      image: 'https://images.unsplash.com/photo-1513475382585-d06e58bcb0e0?w=1200&h=600&fit=crop',
      title: 'Our Story'
    },
    title: 'The Story of Pizza House: A Slice of Happiness',
    storyContent: `<p>Once upon a time, in a small bustling town, there was a cozy little restaurant called Pizza House. It all began with a passionate chef named Marco, who dreamed of sharing his love for authentic Italian pizza with the community.</p>
    <p>Marco had grown up in Naples, where the aroma of freshly baked bread and bubbling tomato sauce was a daily delight. With a small oven and a big heart, Marco opened Pizza House in a humble storefront.</p>
    <p>His secret was simple: use the freshest ingredients, craft each pizza with care, and serve it with a smile. Word quickly spread about his delicious, handcrafted pies topped with ripe tomatoes, fragrant basil, and gooey mozzarella.</p>`
  }
};

// Default Essential Footer data with restaurant-specific content
export const DEFAULT_ESSENTIAL_FOOTER_DATA = {
  restaurantName: "Your Restaurant",
  description: "Experience exceptional dining with our carefully crafted dishes, warm hospitality, and memorable atmosphere. We're committed to serving you the finest cuisine made with fresh, quality ingredients.",
  phone: "(555) 123-4567",
  email: "info@yourrestaurant.com",
  address: "123 Main Street, Your City, State 12345",
  hours: "Mon-Thu: 11am-10pm | Fri-Sat: 11am-11pm | Sun: 12pm-9pm",
  quickLinks: [
    { id: "1", label: "Menu", url: "/menu" },
    { id: "2", label: "Our Story", url: "/our-story" },
    { id: "3", label: "Blog", url: "/blog" },
    { id: "4", label: "Gallery", url: "/gallery" },
    { id: "5", label: "Events", url: "/events" },
    { id: "6", label: "Contact", url: "/contact" }
  ],
  socialLinks: [
    { id: "1", platform: 'facebook', url: "https://facebook.com/yourrestaurant" },
    { id: "2", platform: 'instagram', url: "https://instagram.com/yourrestaurant" },
    { id: "3", platform: 'twitter', url: "https://twitter.com/yourrestaurant" }
  ],
  newsletterTitle: "Stay Updated",
  newsletterDescription: "Subscribe to our newsletter for special offers, new menu items, and exclusive events.",
  showNewsletter: true,
  copyrightText: "© 2024 Paradise Biryani Pointe. All rights reserved.",
  legalLinks: [
    { id: "1", label: "Privacy Policy", url: "/privacy", content: "This Privacy Policy describes how Paradise Biryani Pointe collects, uses, and protects your personal information when you visit our restaurant or use our services." },
    { id: "2", label: "Terms of Service", url: "/terms", content: "These Terms of Service govern your use of Paradise Biryani Pointe's services and facilities. By dining with us, you agree to these terms." },
    { id: "3", label: "Cookie Policy", url: "/cookies", content: "This Cookie Policy explains how Paradise Biryani Pointe uses cookies and similar technologies when you visit our website or use our online services." }
  ],
  backgroundColor: "#1f2937",
  textColor: "#f9fafb",
  linkColor: "#fbbf24",
  borderColor: "#374151"
};

// Enhanced blog data with restaurant-specific content
export const RESTAURANT_BLOG_DATA: BasicBlogWidgetData = {
  title: '<h2 class="text-3xl font-bold text-gray-900 mb-4">Latest Blog Posts</h2>',
  subtitle: '<p class="text-gray-600 text-lg">Discover our latest stories and insights</p>',
  posts: [
    {
      id: '1',
      title: '<h3 class="text-xl font-bold text-gray-900">The Art of Perfect Pizza</h3>',
      content: '<p class="text-gray-700 leading-relaxed">Discover the secrets behind creating the perfect pizza, from <strong>dough preparation</strong> to the finest toppings. Learn about traditional techniques, ingredient selection, and the art of achieving that perfect crispy crust.</p>',
      excerpt: '<p class="text-gray-600">Learn the essential techniques for making <em>restaurant-quality pizza</em> at home.</p>',
      image: 'https://images.unsplash.com/photo-1565299624946-b28f40a0ca4b?w=400&h=250&fit=crop',
      date: new Date().toISOString(),
      readTime: '5 min',
      category: 'Cooking',
      isEnabled: true,
    },
    {
      id: '2',
      title: '<h3 class="text-xl font-bold text-gray-900">Fresh Ingredients Matter</h3>',
      content: '<p class="text-gray-700 leading-relaxed">Why using <strong>fresh, local ingredients</strong> makes all the difference in your culinary creations. Discover how seasonal produce and quality sourcing can elevate every dish to new heights.</p>',
      excerpt: '<p class="text-gray-600">Explore how <em>fresh ingredients</em> elevate every dish to new heights.</p>',
      image: 'https://images.unsplash.com/photo-1540420773420-3366772f4999?w=400&h=250&fit=crop',
      date: new Date(Date.now() - 86400000).toISOString(),
      readTime: '3 min',
      category: 'Ingredients',
      isEnabled: true,
    },
  ],
  postsPerPage: 6,
  showCategory: true,
  showReadTime: true,
  showAuthor: false,
  backgroundColor: '#ffffff',
  textColor: '#1f2937',
  cardBackgroundColor: '#ffffff',
  primaryColor: '#EF4444',
};

// Import complete default site widgets from JSON file
import defaultSiteWidgetsJson from './defaultSiteWidgets.json';

// Complete default site widgets array - used when no widgets exist
export const COMPLETE_DEFAULT_SITE_WIDGETS = defaultSiteWidgetsJson.widgets;

// Extract the raw navigation data for ProtectedLayout
export const DEFAULT_NAV_RAW_DATA = COMPLETE_DEFAULT_SITE_WIDGETS[0].data;

// Enhanced empty state handlers for when no data is available
export const EMPTY_STATE_HANDLERS = {
  // When no menu data is available
  getEmptyMenuData: () => [],
  
  // When no combos are available
  getEmptyCombos: () => [],
  
  // When no popular items are selected
  getEmptyPopularItems: () => ({
    selectedItemIds: [],
    menuData: []
  }),
  
  // Default empty state content for various widgets
  getEmptyStateContent: (widgetType: string) => {
    const emptyStates = {
      combo: '<h3 class="text-lg sm:text-xl font-semibold mb-2">No Combos Available</h3><p class="text-sm sm:text-base max-w-md">Our combo deals will be displayed here soon.</p>',
      popular: '<h3 class="text-lg sm:text-xl font-semibold mb-2">No Popular Items Available</h3><p class="text-sm sm:text-base max-w-md">Our popular items will be displayed here soon.</p>',
      menu: '<h3 class="text-lg sm:text-xl font-semibold mb-2">No Menu Items Available</h3><p class="text-sm sm:text-base max-w-md">Our menu items will be displayed here soon.</p>',
      default: '<h3 class="text-lg sm:text-xl font-semibold mb-2">No Content Available</h3><p class="text-sm sm:text-base max-w-md">Content will be displayed here soon.</p>'
    };
    return emptyStates[widgetType as keyof typeof emptyStates] || emptyStates.default;
  }
};

// Enhanced combo data with multiple variants for different scenarios
export const ENHANCED_COMBO_DATA = {
  // When restaurant has no combos but wants to show the section
  empty: {
    content: '<h2 class="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Our <span class="text-red-600">Special</span> Combos</h2><p class="text-gray-600 text-lg max-w-2xl mx-auto">Discover our amazing combo deals, perfect for sharing and saving</p>',
    backgroundColor: "#ffffff",
    combos: [],
    emptyStateContent: EMPTY_STATE_HANDLERS.getEmptyStateContent('combo')
  },
  
  // Sample combo data for new restaurants
  sample: {
    content: '<h2 class="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Our <span class="text-red-600">Special</span> Combos</h2><p class="text-gray-600 text-lg max-w-2xl mx-auto">Discover our amazing combo deals, perfect for sharing and saving</p>',
    backgroundColor: "#ffffff",
    combos: [
      {
        pk_id: "sample-combo-001",
        title: "Family Feast",
        description: "Perfect for sharing! Get multiple items with sides and drinks for the whole family.",
        products: [
          {
            pk_id: "sample-item-001",
            name: "Main Dish",
            price: 12.99,
            image: "https://images.unsplash.com/photo-1513104890138-7c749659a591?w=400&h=300&fit=crop"
          },
          {
            pk_id: "sample-item-002",
            name: "Side Dish",
            price: 5.99,
            image: "https://images.unsplash.com/photo-1546833999-b9f581a1996d?w=400&h=300&fit=crop"
          },
          {
            pk_id: "sample-item-003",
            name: "Drinks (2x)",
            price: 4.98,
            image: "https://images.unsplash.com/photo-1571997478779-2adcbbe9ab2f?w=400&h=300&fit=crop"
          }
        ],
        combo_price: 19.99,
        offer_percentage: 15,
        combo_image: "https://images.unsplash.com/photo-1513104890138-7c749659a591?w=400&h=300&fit=crop",
        is_enabled: true,
        created_date: new Date().toISOString()
      }
    ],
    emptyStateContent: EMPTY_STATE_HANDLERS.getEmptyStateContent('combo')
  }
};

// Enhanced popular items data with multiple scenarios
export const ENHANCED_POPULAR_ITEMS_DATA = {
  // When no items are selected but menu data exists
  emptySelection: {
    content: '<h2 class="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Our <span class="text-red-600">Popular</span> Items</h2><p class="text-gray-600 text-lg max-w-2xl mx-auto">Discover our most loved dishes, crafted with passion and served with pride</p>',
    backgroundColor: "#ffffff",
    selectedItemIds: [],
    emptyStateContent: EMPTY_STATE_HANDLERS.getEmptyStateContent('popular'),
    showRating: false,
    menuData: []
  },
  
  // When no menu data exists at all
  emptyMenu: {
    content: '<h2 class="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Our <span class="text-red-600">Popular</span> Items</h2><p class="text-gray-600 text-lg max-w-2xl mx-auto">Discover our most loved dishes, crafted with passion and served with pride</p>',
    backgroundColor: "#ffffff",
    selectedItemIds: [],
    emptyStateContent: EMPTY_STATE_HANDLERS.getEmptyStateContent('menu'),
    showRating: false,
    menuData: []
  }
};

// Utility functions for handling empty states
export const getDefaultWidgetData = (widgetType: string, scenario: 'empty' | 'sample' = 'sample') => {
  switch (widgetType) {
    case 'ClassicCombo':
    case 'BasicCombo':
    case 'PrimeCombo':
      return ENHANCED_COMBO_DATA[scenario];
    
    case 'ClassicPopularItems':
    case 'BasicPopularItems':
    case 'PrimePopularItems':
      return scenario === 'empty' ? ENHANCED_POPULAR_ITEMS_DATA.emptySelection : DEFAULT_POPULAR_ITEMS_DATA.data;
    
    default:
      return createDefaultWidgetData(widgetType);
  }
};

// Enhanced function to handle menu data scenarios
export const handleMenuDataScenarios = (menuData: any[], selectedItemIds: string[] = []) => {
  // If no menu data exists
  if (!menuData || menuData.length === 0) {
    return {
      hasMenuData: false,
      hasSelectedItems: false,
      emptyStateType: 'no-menu',
      emptyStateContent: EMPTY_STATE_HANDLERS.getEmptyStateContent('menu')
    };
  }
  
  // If menu data exists but no items are selected
  if (selectedItemIds.length === 0) {
    return {
      hasMenuData: true,
      hasSelectedItems: false,
      emptyStateType: 'no-selection',
      emptyStateContent: EMPTY_STATE_HANDLERS.getEmptyStateContent('popular')
    };
  }
  
  // If menu data exists and items are selected
  const availableItems = menuData.filter(item => 
    selectedItemIds.includes(item.pk_id || item.id) && 
    (item.is_enabled === true || item.is_enabled === 'true')
  );
  
  return {
    hasMenuData: true,
    hasSelectedItems: availableItems.length > 0,
    availableItems,
    emptyStateType: availableItems.length === 0 ? 'no-available-items' : 'has-items',
    emptyStateContent: availableItems.length === 0 ? 
      EMPTY_STATE_HANDLERS.getEmptyStateContent('popular') : null
  };
};

// Export for backward compatibility with existing code
export const defaultWidgets = DEFAULT_PAGE_WIDGETS;

export default DEFAULT_PAGE_WIDGETS;
